import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import CustomModal from '../../../Modal';
import CustomTooltip from '../../../Tooltip';
import DataComponent from '../../../DataComponent/DataComponent';
import TreeGoalActions from '../../../TreeGoalActions/TreeGoalActions';
import config from '../../../../config';
import { getConversionStatuses } from '../../../../helpers/common';
import { getPath } from 'utils/Router';
import { escape } from '../../../../helpers/escape';
import {isMobile} from "utils/DeviceDetect";
import { I18nContext } from 'utils/I18n/context';

const ListPostbacks = ({ 
    headerNames, 
    list, 
    page, 
    total, 
    loading, 
    getListPostbacks, 
    getListGoalActionsByIds, 
    onDeletePostback 
}) => {
    const { t } = useContext(I18nContext);
    const [state, setState] = useState({
        headerNames: headerNames,
        list: list,
        page: page,
        totalPages: total,
        loading: loading,
        deletePostbackId: null,
        deletePostbackTitle: '',
        deleteModalIsVisible: false
    });

    const staticNames = t?.postbackPage || { 
        list: { 
            all: 'All', 
            create: 'Create', 
            limit: 'Limit reached', 
            deleteNoty: 'Delete postback' 
        } 
    };
    const staticNamesActions = t?.actions || { edit: 'Edit', delete: 'Delete' };

    useEffect(() => {
        setState(prev => ({
            ...prev,
            headerNames: headerNames,
            list: list,
            page: page,
            totalPages: total,
            loading: loading
        }));
    }, [headerNames, list, page, total, loading]);

    const onDataTableAction = useCallback((state, actionType) => {
        getListPostbacks(state);
    }, [getListPostbacks]);

    const onDeletePostbackHandler = useCallback(() => {
        onDeletePostback(state);

        setState(prev => ({
            ...prev,
            deleteModalIsVisible: false
        }));
    }, [onDeletePostback, state]);

    const showDeleteModal = useCallback((id, title) => {
        setState(prev => ({
            ...prev,
            deletePostbackId: id,
            deletePostbackTitle: title,
            deleteModalIsVisible: true
        }));
    }, []);

    const closeDeleteModal = useCallback(() => {
        setState(prev => ({
            ...prev,
            deleteModalIsVisible: false
        }));
    }, []);

    const getStatuses = useCallback((list) => {
        if (!list) {
            return staticNames.list.all;
        }

        return <div className="postback__conversion">{getConversionStatuses(list)}</div>;
    }, [staticNames.list.all]);

    const getGoalActions = useCallback((ids) => {
        if (!ids) {
            return staticNames.list.all;
        }

        return <TreeGoalActions data={getListGoalActionsByIds(ids)} />;
    }, [staticNames.list.all, getListGoalActionsByIds]);

    const getPostbacks = useCallback(() => {
        let postbacks = [];

        state.list.map((postback, i) => {
            postbacks.push({
                id: postback.id,
                title: escape(postback.config.title),
                url: postback.config.url,
                method: postback.config.method,
                conversion_statuses: getStatuses(postback.config.conversion_statuses),
                goal_action_ids: getGoalActions(postback.config.goal_action_ids),
                actions: (
                    <div className="row-controls">
                        <CustomTooltip id={`controlEdit-${i}`} tooltip={staticNamesActions.edit}>
                            <Link className="control-item view-button" to={getPath('editPostback', {id: postback.id})}>
                                <span className="fa fa-pencil-square-o"/><span
                                className={'link-title'}>{staticNamesActions.edit}</span>
                            </Link>
                        </CustomTooltip>
                        <CustomTooltip id={`controlDelete-${i}`} tooltip={staticNamesActions.delete}>
                            <a className="control-item delete-button" href="#!" onClick={() => {
                                showDeleteModal(postback.id, postback.config.title)
                            }}>
                                <span className="fa fa-trash-o"/><span
                                className={'link-title'}>{staticNamesActions.delete}</span>
                            </a>
                        </CustomTooltip>
                    </div>
                )
            });
        });

        return postbacks;
    }, [state.list, getStatuses, getGoalActions, staticNamesActions, showDeleteModal]);

    const maxCountPostbacks = config.maxCountPostbacks;

    return (<React.Fragment>
        {state.list.length !== maxCountPostbacks
            ? <Link className="apply-btn btn" to={getPath('createPostback')}>{staticNames.list.create}</Link>
            : <div className="postback__limit"><p className="text-warning">{staticNames.list.limit}</p></div>
        }
        <div className="ibox-content">
            <div className={`postback__table ${isMobile ? 'mobile-view' : ''}`}>
                <CustomModal visible={state.deleteModalIsVisible} onClose={closeDeleteModal} onConfirm={onDeletePostbackHandler}>
                    <p>{staticNames.list.deleteNoty} {'«' + state.deletePostbackTitle + '»'}?</p>
                </CustomModal>
                <DataComponent
                    onAction={onDataTableAction}
                    totalPages={state.totalPages}
                    headerNames={state.headerNames}
                    headerNamesArray={true}
                    autoTile={true}
                    data={getPostbacks()}
                    loading={state.loading}
                    className="simple-table"
                />
            </div>
        </div>
    </React.Fragment>)
};

ListPostbacks.propTypes = {
    headerNames: PropTypes.array,
    list: PropTypes.array,
    page: PropTypes.number,
    total: PropTypes.number,
    loading: PropTypes.bool,
    getListPostbacks: PropTypes.func,
    getListGoalActionsByIds: PropTypes.func,
    onDeletePostback: PropTypes.func
};

ListPostbacks.defaultProps = {
    headerNames: [],
    list: [],
    page: 1,
    total: 0,
    loading: false,
    getListPostbacks: () => {},
    getListGoalActionsByIds: () => {},
    onDeletePostback: () => {}
};

export { ListPostbacks };
